(() => {
  const SELECTOR = '[data-remote-src], [data-remote-srcset], [data-remote-href]';
  const LOADED_ATTR = 'data-remote-loaded';
  const PRIORITY_HIGH = 'high';
  const STRATEGY_IDLE = 'idle';
  const STRATEGY_VISIBLE = 'visible';
  const STRATEGY_INTERACTION = 'interaction';
  const IO_ROOT_MARGIN = '320px 0px';

  const candidates = new Set();
  const waitingPlaceholders = new Set();
  const targetDependencies = new WeakMap();
  let intersectionObserver = null;
  let globalInteractionPromise = null;

  function waitForInteraction() {
    if (globalInteractionPromise) {
      return globalInteractionPromise;
    }
    globalInteractionPromise = new Promise((resolve) => {
      const events = ['pointerdown', 'touchstart', 'keydown'];
      const options = { passive: true, once: true };
      const handler = () => {
        events.forEach((eventName) => {
          window.removeEventListener(eventName, handler, options);
        });
        resolve();
      };
      events.forEach((eventName) => {
        window.addEventListener(eventName, handler, options);
      });
    });
    return globalInteractionPromise;
  }

  function scheduleIdle(callback) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 1500 });
    } else {
      window.setTimeout(callback, 600);
    }
  }

  function markLoaded(element) {
    element.setAttribute(LOADED_ATTR, 'true');
    candidates.delete(element);
    waitingPlaceholders.delete(element);
  }

  function updateSources(element) {
    if (!element || element.getAttribute(LOADED_ATTR) === 'true') return;

    const remoteSrc = element.getAttribute('data-remote-src');
    const remoteSrcset = element.getAttribute('data-remote-srcset');
    const remoteHref = element.getAttribute('data-remote-href');

    if (remoteSrc) {
      element.setAttribute('src', remoteSrc);
    }

    if (remoteSrcset) {
      element.setAttribute('srcset', remoteSrcset);
    }

    if (remoteHref) {
      element.setAttribute('href', remoteHref);
    }

    if (
      element.tagName === 'SOURCE' &&
      (remoteSrc || remoteSrcset)
    ) {
      const parentMedia = element.parentElement;
      if (parentMedia && typeof parentMedia.load === 'function') {
        parentMedia.load();
      }
    }

    markLoaded(element);
  }

  function loadImmediately(element) {
    updateSources(element);
  }

  function ensureObserver() {
    if (intersectionObserver || !('IntersectionObserver' in window)) {
      return intersectionObserver;
    }

    intersectionObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting && entry.intersectionRatio === 0) {
          return;
        }
        observer.unobserve(entry.target);
        const dependents = targetDependencies.get(entry.target);
        if (dependents && dependents.size) {
          dependents.forEach((placeholder) => updateSources(placeholder));
          targetDependencies.delete(entry.target);
        }
        updateSources(entry.target);
      });
    }, { rootMargin: IO_ROOT_MARGIN });

    return intersectionObserver;
  }

  function getTargets(placeholder) {
    const dataset = placeholder.dataset || {};
    const selector = dataset.remoteTarget;
    if (!selector) {
      if (
        placeholder.tagName === 'SOURCE' &&
        placeholder.parentElement &&
        ['VIDEO', 'AUDIO', 'PICTURE'].includes(placeholder.parentElement.tagName)
      ) {
        return [placeholder.parentElement];
      }
      return [placeholder];
    }
    return Array.from(document.querySelectorAll(selector)).filter(Boolean);
  }

  function setupInteractionTrigger(element) {
    const selector = element.dataset.remoteTarget;
    if (!selector || element.dataset.remoteListening === 'pending') {
      return null;
    }
    const nodes = Array.from(document.querySelectorAll(selector)).filter(Boolean);
    if (!nodes.length) {
      return null;
    }
    element.dataset.remoteListening = 'pending';
    return new Promise((resolve) => {
      const cleanup = () => {
        nodes.forEach((node) => {
          node.removeEventListener('pointerdown', pointerHandler);
          node.removeEventListener('click', pointerHandler);
          node.removeEventListener('keydown', keyHandler);
        });
        element.dataset.remoteListening = 'resolved';
      };
      const finish = () => {
        cleanup();
        resolve();
      };
      const pointerHandler = () => finish();
      const keyHandler = (event) => {
        const key = event?.key;
        if (key === 'Enter' || key === ' ') {
          finish();
        }
      };
      nodes.forEach((node) => {
        node.addEventListener('pointerdown', pointerHandler, { once: true, passive: true });
        node.addEventListener('click', pointerHandler, { once: true, passive: true });
        node.addEventListener('keydown', keyHandler, { passive: true });
      });
    });
  }

  function recheckWaitingPlaceholders() {
    if (!waitingPlaceholders.size) {
      return;
    }
    waitingPlaceholders.forEach((placeholder) => {
      if (placeholder.getAttribute(LOADED_ATTR) === 'true') {
        waitingPlaceholders.delete(placeholder);
        return;
      }
      if (!placeholder.isConnected) {
        waitingPlaceholders.delete(placeholder);
        return;
      }
      const strategy = placeholder.dataset.remoteStrategy || STRATEGY_VISIBLE;
      if (strategy === STRATEGY_INTERACTION) {
        const triggerPromise = setupInteractionTrigger(placeholder);
        if (triggerPromise) {
          placeholder.dataset.lazyWaiting = 'false';
          waitingPlaceholders.delete(placeholder);
          triggerPromise.then(() => updateSources(placeholder));
        }
        return;
      }
      const targets = getTargets(placeholder);
      if (targets.length) {
        placeholder.dataset.lazyWaiting = 'false';
        waitingPlaceholders.delete(placeholder);
        scheduleVisible(placeholder);
      }
    });
  }

  function scheduleVisible(element) {
    const targets = getTargets(element);
    if (!targets.length) {
      if (element.dataset.lazyOptional === 'true') {
        if (element.dataset.lazyWaiting !== 'true') {
          element.dataset.lazyWaiting = 'true';
          waitingPlaceholders.add(element);
        }
        return;
      }
      waitForInteraction().then(() => updateSources(element));
      return;
    }
    element.dataset.lazyWaiting = 'false';
    waitingPlaceholders.delete(element);
    const observer = ensureObserver();
    if (observer) {
      targets.forEach((target) => {
        if (!target) {
          return;
        }
        if (target === element) {
          observer.observe(element);
          return;
        }
        let dependents = targetDependencies.get(target);
        if (!dependents) {
          dependents = new Set();
          targetDependencies.set(target, dependents);
        }
        dependents.add(element);
        observer.observe(target);
      });
    } else {
      scheduleIdle(() => updateSources(element));
    }
  }

  function scheduleLoad(element) {
    if (!element || element.getAttribute(LOADED_ATTR) === 'true') {
      return;
    }
    if (!candidates.has(element)) {
      candidates.add(element);
    }

    const priority = element.getAttribute('data-remote-priority');
    if (priority === PRIORITY_HIGH) {
      loadImmediately(element);
      return;
    }

    const strategy = element.getAttribute('data-remote-strategy') || STRATEGY_VISIBLE;
    if (strategy === STRATEGY_IDLE) {
      scheduleIdle(() => updateSources(element));
      return;
    }
    if (strategy === STRATEGY_INTERACTION) {
      const triggerPromise = setupInteractionTrigger(element);
      if (triggerPromise) {
        triggerPromise.then(() => updateSources(element));
      } else if (element.dataset.remoteTarget) {
        element.dataset.lazyWaiting = 'true';
        waitingPlaceholders.add(element);
      } else {
        waitForInteraction().then(() => updateSources(element));
      }
      return;
    }

    scheduleVisible(element);
  }

  function scanNode(node) {
    if (!node || node.nodeType !== Node.ELEMENT_NODE) return;

    if (node.matches(SELECTOR)) {
      scheduleLoad(node);
    }

    node
      .querySelectorAll(SELECTOR)
      .forEach((element) => scheduleLoad(element));
  }

  function initialise() {
    scanNode(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((addedNode) => {
          scanNode(addedNode);
        });
      });
      recheckWaitingPlaceholders();
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
